import React, {useRef} from "react";
import {graphQLServices} from "../../services/GraphQLServices";
import {Button, Checkbox, Paper, Stack, Tooltip, Typography} from "@mui/material";
import {Link} from "react-router-dom";
import {GlobalState} from "../../global/GlobalState";
import {GET_ORDERS, REQUEST_ORDER_SUMMARY_EXPORT} from "./GraphQLQueries";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {GraphQlDataGridHandle, GraphQlDataGrid} from "../../components/common/grid/GraphQlDataGrid";
import {
	adnResColumn,
	copyableColumn,
	orderStatusColumn,
	textColumn,
	tooltipColumn,
	uuidColumn
} from "../../components/common/grid/Columns";
import {formatOrderStatus} from "../../localization/Translations";
import {translateFunction} from "../../localization/TranslationService";
import {CurrentStageDisplay, HeaderSlot} from "../../components/onsite/Footer";
import {GridColDef} from "@mui/x-data-grid";
import {OrderSummaryExportRequest} from "./models/OrderSummaryExportRequest";
import {toast} from "react-toastify";
import {IncentiveDataExport} from "./orderPackageComponets/IncentiveData";
import {
	Accessibility, Accessible,
	AttachMoneyTwoTone,
	Business,
	Cottage,
	DirectionsCar,
	ElectricBolt,
	LocalHospital,
	QuestionMark,
	TapAndPlay,
	WbSunny,
	Work
} from "@mui/icons-material";

const translate = translateFunction("SureBasePortalOrders");

function OrdersOverviewPageContent() {
	function itemConverter(item: any) {
		return {
			productCategory: item.productCategory,
			creationDateTime: item.creationDateTime,
			transactionDate: item.transactionDate,
			id: item.id,
			surname: item.surname,
			email: item.email,
			mainApplicant: item.mainApplicant,
			postalCode: item.mainApplicant?.address?.postalCode,
			package: item.package,
			productDescription: item.package?.productDescription,
			status: item.status,
			underwritingResult: item.underwritingResult,
			policyId: item.policyId,
			orderType: item.orderType,
			apiClientName: item.apiClientName,
			processReference: item.processReference,
			providerKrn: item.providerKrn,
			supplierName: item.supplierName,
			userDesiresIncentive: item.userDesiresIncentive
		};
	}
	
	const dataGrid = useRef<GraphQlDataGridHandle>(null);
	
	// all: tooltip
	// all: copy button
	// uuid: monospace
	// datetime: format
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Order"),
			renderCell: (params) => (
				<Link to={`/orders/${encodeURIComponent(params.row.id)}`} style={{
					textDecoration: "initial",
					width: "100%",
					height: "100%",
				}}>
					<Button
						variant="text"
						size="small"
						// style={{ marginLeft: 16 }}
						tabIndex={params.hasFocus ? 0 : -1}
						style={{
							width: "100%",
							height: "100%",
							justifyContent: "flex-start"
						}}
					>
						<Stack direction={"row"} alignItems={"center"} gap={1} justifyContent={"flex-start"}>
							<ProductCategoryCell value={params.row.productCategory}/>
						</Stack>
					</Button>
				</Link>
			),
			minWidth: 60,
			maxWidth: 60,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date"),
			type: "dateTime",
			minWidth: 160,
			renderCell: (params) => (
				<Link to={`/orders/${encodeURIComponent(params.row.id)}`} style={{
					textDecoration: "initial",
					width: "100%",
					height: "100%",
				}}>
					<Button
						variant="text"
						size="small"
						// style={{ marginLeft: 16 }}
						tabIndex={params.hasFocus ? 0 : -1}
						style={{
							width: "100%",
							height: "100%",
							justifyContent: "flex-start"
						}}
					>
						{tooltipColumn(textColumn())(params)}
					</Button>
				</Link>
			),
			valueFormatter: (params) => GlobalState.formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "id",
			headerName: translate("Order ID"),
			minWidth: 330,
			renderCell: copyableColumn(tooltipColumn(uuidColumn())),
			flex: 1,
			uuid: true,
		} as any,
		{
			field: "productCategory",
			headerName: translate("Category"),
			type: "singleSelect",
			minWidth: 80,
			renderCell: (params) => <ProductCategoryCell value={params.value}/>,
			valueOptions: ["NON_SPECIFIC", "CAR", "BUSINESS_CAR", "BUSINESS_PACKAGE", "ENERGY", "HOME", "LIABILITY", "TRIPLE_PLAY", "TRAVEL", "HEALTH_CARE", "SOLAR_PANELS", "SAVINGS", "EVENT_BASED"],
			flex: 1,
		},
		{
			field: "policyId",
			headerName: translate("Policy ID"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "surname",
			headerName: translate("Person"),
			minWidth: 180,
			renderCell: (params) => <div>
				{params.value && <div>{params.value}</div>}
				{params.row.email && <Typography variant={"caption"}>{params.row.email}</Typography>}
			</div>,
			flex: 1,
		},
		{
			field: "email",
			headerName: translate("Email"),
			minWidth: 180,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "mainApplicant.address.postalCode",
			headerName: translate("Address"),
			minWidth: 180,
			renderCell: (params) => {
				const address = params.row?.mainApplicant?.address;
				const postalCode = address?.postalCode;
				const houseNumber = address?.houseNumber;
				const houseNumberAddition = address?.houseNumberAddition;
				const street = address?.street;
				const city = address?.city;
				return <div>
					<div>{street} {houseNumber} {houseNumberAddition}</div>
					<Typography variant={"caption"}>{postalCode} {city}</Typography>
				</div>;
			},
			flex: 1,
		},
		{
			field: "package.productDescription",
			headerName: translate("Product"),
			minWidth: 180,
			renderCell: (params) => {
				const companyNameDescription = params.row.package?.companyNameDescription;
				const productDescription = params.row.package?.productDescription;
				return <div>
					<div>{companyNameDescription}</div>
					<Typography variant={"caption"}>{productDescription}</Typography>
				</div>;
			},
			flex: 1,
		},
		{
			field: "orderType",
			headerName: translate("Order Type"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueOptions: ["AFFILIATE", "FUNNEL_MGA"],
			flex: 1,
		},
		{
			field: "transactionDate",
			headerName: translate("Transaction Date"),
			type: "dateTime",
			minWidth: 220,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => GlobalState.formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "status",
			headerName: translate("Status"),
			minWidth: 200,
			renderCell: tooltipColumn(orderStatusColumn()),
			type: "singleSelect",
			valueOptions: ["QUEUED", "PROCESSING", "QUEUED_FOR_SAVING", "ERROR", "UNDER_REVIEW", "DONE"],
			valueFormatter: (params) => formatOrderStatus(params.value),
			flex: 1,
		},
		{
			field: "underwritingResult",
			headerName: translate("UnderwritingResult"),
			minWidth: 200,
			type: "singleSelect",
			valueOptions: [{label: "Accepted", value: "V1"}, {label: "Rejected", value: "V2"}, {label: "Under Review", value: "V3"}],
			renderCell: adnResColumn(tooltipColumn(textColumn())),
			valueFormatter: (params) => {
				if (params.value === "V1")
					return "Accepted";
				else if (params.value === "V2")
					return "Rejected";
				else if (params.value === "V3")
					return "Under Review";
				return "Unknown";
			},
			flex: 1,
		},
		{
			field: "apiClientName",
			headerName: translate("ClientName"),
			minWidth: 180,
			flex: 1,
		},
		{
			field: "processReference",
			headerName: translate("Process Reference"),
			minWidth: 180,
			flex: 1,
		},
		{
			field: "userDesiresIncentive",
			headerName: translate("UserDesiresIncentive"),
			type: "boolean",
			renderCell: (params) => {
				return <Checkbox
					checked={params.value}
					disabled
				/>;
			},
			filterable: true,
			minWidth: 180,
			flex: 1,
		},
		{
			field: "supplierName",
			headerName: translate("SupplierName"),
			minWidth: 180,
			flex: 1
		},
		{
			field: "package.customProperties.providerKrn",
			headerName: translate("ProviderKrn"),
			minWidth: 180,
			flex: 1,
			renderCell: (params) => {
				const providerKrn = params.row.package?.customProperties?.providerKrn;
				
				return <div>
					<div>{providerKrn}</div>
				</div>;
			}
		},
		{
			field: "package.customProperties.productKrn",
			headerName: translate("ProductKrn"),
			minWidth: 180,
			flex: 1,
			renderCell: (params) => {
				const productKrn = params.row.package?.customProperties?.productKrn;
				
				return <div>
					<div>{productKrn}</div>
				</div>;
			}
		},
	];
	
	const handleExportOrderSummary = async (graphQlWhereClause: any) => {
		if (!GlobalState.userSession?.user.emailAddress)
			throw new Error("userSession emailAddress not valid");
		const graphQl = JSON.stringify(graphQlWhereClause);
		await exportOrderSummary({
			graphQlWhereClause: graphQl,
			recipients: [GlobalState.userSession?.user.emailAddress]
		}).then(async (x) => {
			if (!x) {
				throw new Error(`Could not perform export`);
			}
			toast.success(`Export requested, you will receive an email.`);
		});
	};
	
	return <>
		<HeaderSlot><CurrentStageDisplay/></HeaderSlot>
		<Paper style={{
			height: "100%",
		}}>
			<GraphQlDataGrid
				ref={dataGrid}
				toolbarElements={<Stack gap={1} direction={"row"} alignItems={"start"}>
					<Button
						variant={"outlined"}
						onClick={() => handleExportOrderSummary(dataGrid.current?.graphQlWhereClause())}
					>export</Button>
					
					<IncentiveDataExport
						whereClause={() => dataGrid.current?.graphQlWhereClause()}
					/>
					<Button
						variant={"outlined"}
						component={Link}
						to={"/orders/update-underwriting-result"}
					>Update underwriting result</Button>
				</Stack>}
				rows={[]}
				query={GET_ORDERS}
				graphFunction={"orderDetails"}
				client={graphQLServices.orderClient}
				rowsPerPageOptions={[50, 100, 250]}
				initialPageSize={50}
				columns={columns}
				itemConverter={itemConverter}
				defaultSortModel={[
					{
						field: "creationDateTime",
						sort: "desc"
					}
				]}
				initialState={{
					columns: {
						columnVisibilityModel: {
							id: true,
							productCategory: false,
							policyId: false,
							email: true,
							transactionDate: false,
							underwritingResult: true,
							userDesiresIncentive: true,
						},
					},
				}}
				sx={{
					// remove padding of first two columns (link and creation date) allowing the buttons to reach each other
					"& .MuiDataGrid-cell:nth-child(1)": {paddingRight: 0},
					"& .MuiDataGrid-cell:nth-child(2)": {paddingLeft: 0}
				}}
			/>
		</Paper>
	</>;
}

async function exportOrderSummary(orderSummaryExportRequest: OrderSummaryExportRequest): Promise<boolean> {
	const existsItems = await graphQLServices.orderClient.query(
		{
			query: REQUEST_ORDER_SUMMARY_EXPORT,
			variables: {
				orderSummaryExportRequest
			},
			fetchPolicy: "no-cache"
		}
	);
	const result: { requestOrderSummaryExport: { queued: boolean } } = existsItems.data;
	return result.requestOrderSummaryExport.queued ?? false;
}

export const OrdersOverviewPage = pageAuthorizedByGroup(OrdersOverviewPageContent, ["Admin", "Order"]);

function ProductCategoryCell(props: { value: string }) {
	return <Tooltip title={props.value}>
		<Stack alignItems={"center"}>
			<ProductCategoryIcon value={props.value}/>
		</Stack>
	</Tooltip>;
}

function ProductCategoryIcon(props: { value: string }) {
	if (props.value === "NON_SPECIFIC")
		return <QuestionMark/>;
	if (props.value === "CAR")
		return <DirectionsCar/>;
	if (props.value === "BUSINESS_CAR")
		return <DirectionsCar/>;
	if (props.value === "BUSINESS_PACKAGE")
		return <Business/>;
	if (props.value === "ENERGY")
		return <ElectricBolt/>;
	if (props.value === "HOME")
		return <Cottage/>;
	if (props.value === "LIABILITY")
		return <Accessibility/>;
	if (props.value === "TRIPLE_PLAY")
		return <TapAndPlay/>;
	if (props.value === "TRAVEL")
		return <Work/>;
	if (props.value === "HEALTH_CARE")
		return <LocalHospital/>;
	if (props.value === "SOLAR_PANELS")
		return <WbSunny/>;
	if (props.value === "SAVINGS")
		return <AttachMoneyTwoTone/>;
	if (props.value === "EVENT_BASED")
		return <Accessible/>;
	
	return <QuestionMark/>;
}
